import { endOfDay, format, max, startOfDay } from "date-fns";
import { TFunction } from "i18next";

import { CertificationAndEliglibilityType } from "@/app/lib/format-attributes";
import { AppDateFormats } from "@/app/lib/format-date";
import { FormattedOption } from "@/app/lib/format-rec-options";
import { OrderExpiry, OrderType, RecAttributes, RegistryCode } from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, ANY_REGISTRY_CODE } from "@/app/types/okta.types";

export const utcEndOfDay = new Date(new Date().setUTCHours(23, 59, 59, 999)).toISOString();

export const orderExpiriesOptions: FormattedOption = [
  { label: "N/A", value: "" },
  { label: `${OrderExpiry.Gtc} (Good Till Cancelled)`, value: OrderExpiry.Gtc },
  {
    label: `${OrderExpiry.Gtd} (Good Till Today)`,
    value: OrderExpiry.Gtd,
  },
  { label: `Custom`, value: OrderExpiry.Custom },
];

export const orderTypeOptions: FormattedOption = [
  { label: `${OrderType.Limit} (Default)`, value: OrderType.Limit },
  {
    label: `${OrderType.Aon} (All or None)`,
    value: OrderType.Aon,
  },
  {
    label: `${OrderType.Fok} (Fill or Kill)`,
    value: OrderType.Fok,
  },
  {
    label: `${OrderType.Ioc} (Immediate or Cancel)`,
    value: OrderType.Ioc,
  },
];
export const yesNoOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

/**
 *
 * Return minTime based on current Date (if selected date is current Date) or based on selected date at 11:59PM
 * Max time is always end of today / selectedDate
 * @param selectedDate
 * @param options
 * @returns
 */
export const getMinAndMaxTimeForCustomOrderExpiry = (selectedDate?: string | null) => {
  const today = new Date();
  if (selectedDate) {
    const minTime = max([today, startOfDay(new Date(selectedDate))]);
    return [minTime, endOfDay(new Date(selectedDate))];
  }
  return [today, endOfDay(today)];
};

export const isOrderExpiryNotApplicable = (orderType: OrderType) => {
  return [OrderType.Fok, OrderType.Ioc].includes(orderType);
};

export const getDefaultProductAttributeValue = (t: TFunction, values?: string[] | null) => {
  if (!values?.length) {
    return t("Any");
  }

  return values.join(", ");
};

export const getProductAttributesAsDisplayAttributes = (
  t: TFunction,
  tradeContract: {
    attributes: RecAttributes;
    formattedAttributes: RecAttributes & { certificationsAndEligibilities: CertificationAndEliglibilityType[] };
    activeRegistryCode: AggregatedRegistryCode;
    activeRegistryDisplayName: string;
  },
) => {
  const isActiveRegistryErcot = tradeContract.activeRegistryCode === RegistryCode.Ercot;
  const isActiveRegistryAny = tradeContract.activeRegistryCode === ANY_REGISTRY_CODE;
  return [
    {
      label: "Product",
      value: `${tradeContract.activeRegistryDisplayName || "Any"} REC`,
    },
    {
      label: "Vintage",
      value: getDefaultProductAttributeValue(t, tradeContract.formattedAttributes.vintages),
    },
    {
      label: isActiveRegistryErcot ? "Certification" : "Eligibility",
      value: getDefaultProductAttributeValue(
        t,
        tradeContract.formattedAttributes.certificationsAndEligibilities.flatMap(({ value }) => value),
      ),
    },
    ...(isActiveRegistryErcot || isActiveRegistryAny
      ? [
          {
            label: "Certificate Type",
            value: getDefaultProductAttributeValue(t, tradeContract.formattedAttributes.certificateTypes),
          },
        ]
      : []),
    ...(!isActiveRegistryErcot || isActiveRegistryAny
      ? [
          {
            label: "Location",
            value: getDefaultProductAttributeValue(t, tradeContract.formattedAttributes.locations),
          },
        ]
      : []),
    {
      label: isActiveRegistryErcot ? "Technology Type" : "Fuel Type",
      value: getDefaultProductAttributeValue(t, tradeContract.formattedAttributes.fuelSources),
    },
    {
      label: "Commencement of Operations Date (COD)",
      value: tradeContract.attributes.commencementDate
        ? t(`Not older than {{cod}}`, {
            cod: format(new Date(tradeContract.attributes.commencementDate), AppDateFormats.AbbreviatedMonthFormat),
          })
        : t("Any"),
    },
  ];
};
